import { Heap } from '@datacamp/exercise-tracking';
import { useCallback, useEffect, useRef, useState } from 'react';
const LOCAL_STORAGE_KEY = {
  AI_COACH: 'showAiCoach',
  TRANSCRIPT: 'showDynamicTranscript'
};
const getSidePanelPreference = sidePanel => {
  switch (localStorage.getItem(LOCAL_STORAGE_KEY[sidePanel])) {
    case 'true':
      return 'OPEN';
    case 'false':
      return 'CLOSED';
    default:
      return 'NO_PREFERENCE';
  }
};
export const useSidePanel = _ref => {
  let {
    exerciseId,
    isAiCoachEnabled,
    isAiCoachLoading,
    isLoggedIn,
    onSetPlaying
  } = _ref;
  const [activeSidePanel, setActiveSidePanel] = useState(null);
  const prevActiveSidePanel = useRef(null);
  const trackStateChangeEvent = useCallback((panel, event) => {
    Heap.trackEvent({
      heapEventProperties: {
        event_category: 'experiment',
        exerciseId,
        isAiCoachEnabled,
        panel
      },
      name: "Learn - VideoExercise - Side Panel ".concat(event)
    });
  }, [exerciseId, isAiCoachEnabled]);
  useEffect(() => {
    if (activeSidePanel === prevActiveSidePanel.current) {
      return;
    }

    // Note: no early returns because we want to track
    // both Closed and Opened events when switching between panels.
    if (prevActiveSidePanel.current !== null) {
      trackStateChangeEvent(prevActiveSidePanel.current, 'Closed');
    }
    if (activeSidePanel !== null) {
      trackStateChangeEvent(activeSidePanel, 'Opened');
    }
    prevActiveSidePanel.current = activeSidePanel;
  }, [activeSidePanel, trackStateChangeEvent]);
  useEffect(() => {
    // Do not open a side panel yet if the AI coach data is still loading.
    // We need this data to determine if we should open AI Coach or Transcript.
    if (isAiCoachEnabled && isAiCoachLoading) {
      return;
    }
    const transcriptPreference = getSidePanelPreference('TRANSCRIPT');
    const aiCoachPreference = getSidePanelPreference('AI_COACH');
    if (isAiCoachEnabled && (aiCoachPreference === 'OPEN' || aiCoachPreference === 'NO_PREFERENCE')) {
      // User has not chosen to hide the AI coach so we show it by default
      setActiveSidePanel('AI_COACH');
      return;
    }

    // If the user has a saved preference in local storage we use that value
    if (transcriptPreference !== 'NO_PREFERENCE') {
      setActiveSidePanel(transcriptPreference === 'OPEN' ? 'TRANSCRIPT' : null);
      return;
    }
    if (!isLoggedIn) {
      setActiveSidePanel('TRANSCRIPT');
      return;
    }
    setActiveSidePanel(null);
  }, [isAiCoachEnabled, isAiCoachLoading, isLoggedIn]);
  const togglePanel = panel => {
    const newState = activeSidePanel === panel ? null : panel;

    // Pause the video if the user opens the AI Coach
    // and resume playing if the user closes it.
    if (panel === 'AI_COACH' || activeSidePanel === 'AI_COACH') {
      onSetPlaying(newState !== 'AI_COACH');
    }
    setActiveSidePanel(newState);
    localStorage.setItem(LOCAL_STORAGE_KEY[panel], newState === panel ? 'true' : 'false');
  };
  return {
    activeSidePanel,
    togglePanel
  };
};